import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../../../connector/api';
import GradeCategoryModal from '../modal/GradeCategoryModal';
import DeleteConfirmModal from '../modal/DeteleConfirmModal';

export default function GradeCategoryTab() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);

    // State for delete modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const res = await api.get('grade-categories/');
            setCategories(res.data.results || res.data || []);
        } catch (err) {
            console.error("Failed to fetch grade categories:", err);
            toast.error("Error loading grade categories.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenCreateModal = () => {
        setSelectedCategory(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (category) => {
        setSelectedCategory(category);
        setIsModalOpen(true);
    };

    const handleOpenDeleteModal = (category) => {
        setCategoryToDelete(category);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!categoryToDelete) return;
        setIsDeleting(true);
        try {
            await api.delete(`grade-categories/${categoryToDelete.id}/`);
            toast.success("Category deleted successfully!");
            fetchCategories();
            setIsDeleteModalOpen(false);
        } catch (err) {
            console.error("Failed to delete category:", err);
            toast.error("Failed to delete category.");
        } finally {
            setIsDeleting(false);
            setCategoryToDelete(null);
        }
    };

    // Calculate total weight percentage across active categories
    const totalWeight = categories
        .filter(c => c.is_active)
        .reduce((sum, c) => sum + parseFloat(c.weight || 0), 0);

    return (
        <div className="space-y-6">
            {/* Header & Primary Action */}
            <div className="bg-white border border-zinc-200 rounded-lg p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-900 tracking-tight">
                        Grade Categories
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Configure weighted assignment groups (e.g., Exams, Homework, Quizzes).
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right hidden sm:block">
                        <span className="text-[10px] text-zinc-400 block font-medium uppercase tracking-wider">Total Weight</span>
                        <span className={`text-xs font-bold ${totalWeight === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {totalWeight.toFixed(2)}% / 100%
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleOpenCreateModal}
                        className="px-3.5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        + Add Category
                    </button>
                </div>
            </div>

            {/* Category Table */}
            {loading ? (
                <div className="p-8 text-center text-xs text-zinc-500 bg-white border border-zinc-200 rounded-xl">
                    Loading grade categories...
                </div>
            ) : categories.length === 0 ? (
                <div className="p-8 text-center text-xs text-zinc-500 bg-white border border-zinc-200 rounded-xl">
                    No grade categories found. Click above to create one.
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-zinc-700">
                            <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold text-[10px]">
                                <tr>
                                    <th className="py-3 px-4">Category Name</th>
                                    <th className="py-3 px-4">Weight (%)</th>
                                    <th className="py-3 px-4">Color Tag</th>
                                    <th className="py-3 px-4">Status</th>
                                    <th className="py-3 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {categories.map((cat) => (
                                    <tr key={cat.id} className="hover:bg-zinc-50/50 transition-colors">
                                        <td className="py-3 px-4">
                                            <div className="flex flex-col">
                                                {/* Category Name */}
                                                <span className="font-bold text-zinc-900 text-xs">
                                                    {cat.name}
                                                </span>
                                                
                                                {/* Category Description */}
                                                {cat.description ? (
                                                    <span className="text-[11px] text-zinc-500 font-normal mt-0.5 line-clamp-1">
                                                        {cat.description}
                                                    </span>
                                                ) : (
                                                    <span className="text-[11px] text-zinc-300 italic font-normal mt-0.5">
                                                        No description
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 font-semibold text-zinc-700 whitespace-nowrap">
                                            {parseFloat(cat.weight).toFixed(2)}%
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                                                    style={{ backgroundColor: cat.color || '#3B82F6' }}
                                                />
                                                <span className="font-mono text-[11px] text-zinc-500">
                                                    {cat.color}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 whitespace-nowrap">
                                            {cat.is_active ? (
                                                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                                    Active
                                                </span>
                                            ) : (
                                                <span className="text-[10px] font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-md">
                                                    Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenEditModal(cat)}
                                                    className="px-1.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                                                >
                                                    <Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenDeleteModal(cat)}
                                                    className="px-1.5 py-1 text-xs font-medium text-rose-600 hover:text-rose-700"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <GradeCategoryModal
                    category={selectedCategory}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchCategories();
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Grade Category"
                message={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
                isDeleting={isDeleting}
            />
        </div>
    );
}